import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-950">分类未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />

      <main>
        {/* Category Header */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-80 flex-shrink-0">
                <div className="h-48 lg:h-56 rounded-lg overflow-hidden">
                  <img
                    src={category.imageUrl}
                    alt={category.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">{category.name}</h1>
                <p className="mt-4 text-foreground-600 leading-relaxed text-sm max-w-2xl">
                  {category.description}
                </p>
                <p className="mt-4 text-sm text-foreground-500">
                  共 {categoryArticles.length} 篇文章
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex overflow-x-auto gap-1 py-3 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm rounded-full whitespace-nowrap transition-colors duration-200 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group flex flex-col sm:flex-row bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300/60 transition-all duration-300"
                  >
                    <div className="sm:w-44 h-44 sm:h-auto flex-shrink-0 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 p-5 flex flex-col justify-between">
                      <div>
                        <h2 className="text-base font-heading text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {article.title}
                        </h2>
                        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {article.summary}
                        </p>
                      </div>
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 bg-background-100 text-foreground-600 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
                        <span>{article.author}</span>
                        <span>{article.publishDate}</span>
                        <span>{article.readTime} 分钟阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}